import "@fontsource/roboto" // Roboto
import "@fontsource/open-sans" // Open Sans
import "@fontsource/merriweather" // Merriweather
import "@fontsource/playfair-display" // Playfair Display
import "@fontsource/montserrat" // Montserrat
import "@fontsource/lato" // Lato
import "@fontsource/poppins" // Poppins

import React, { useRef, useCallback, useEffect, useState, useMemo } from "react"
import { Circle, Line, Stage, Layer, Text, Rect, Image as KonvaImage, Transformer, Group } from "react-konva"
import { doc, getDoc } from "firebase/firestore"
import { db, USER_TYPES } from "./firebase"
import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import { FaSave, FaUndo, FaRedo } from "react-icons/fa"
import {
  Auth,
  AuthProvider,
  useAuth,
  StudioManager,
  DesignGalleryModal,
  SaveDesignDialog,
  MemberDesignModal,
  DesignPreviewModal,
  ExportModal,
  ConfirmationModal,
  FontSelector,
  ErrorBoundary,
  ToastProvider,
  useToast,
  NotificationBell,
} from "./components"
import { useLocalStorage, useUndoRedo, useCanvasState, useDesignManagement, useStudioManagement, useModalStates } from "./hooks"
import { saveDesign as saveDesignUtil, updateDesign, deleteDesign as deleteDesignUtil, STUDIO_ROLES } from "./utils/firebaseUtils"

const CUTLINE_MARGIN = 10
const SAFEZONE_MARGIN = 30
const SNAP_THRESHOLD = 100

function MainApp() {
  const { currentUser, userProfile, logout, updateDisplayName } = useAuth()
  const toast = useToast()

  // Use custom hooks for state management
  const { elements, setElements, clearElements } = useLocalStorage(currentUser?.uid)
  const { history, redoStack, addToHistory, undo, redo } = useUndoRedo(elements)
  const canvasState = useCanvasState()
  const designManagement = useDesignManagement(currentUser)
  const studioManagement = useStudioManagement(currentUser, designManagement.generateThumbnail, designManagement.designThumbnails)
  const modalStates = useModalStates()

  // Profile editing state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editingNameValue, setEditingNameValue] = useState("")

  // Guidance banner for non-admin studio members
  const [showGuidanceBanner, setShowGuidanceBanner] = useState(() => {
    return !localStorage.getItem("adGuidanceDismissed")
  })

  // Refs
  const transformerRef = useRef(null)
  const stageRef = useRef(null)
  const selectionStartRef = useRef(null)
  const groupDragOffset = useRef({})
  const fileInputRef = useRef(null)
  const inputRef = useRef(null)
  const imageCache = useRef(new Map())

  // Build memoized image objects for all image elements to prevent flicker
  const loadedImages = useMemo(() => {
    const cache = imageCache.current
    const result = new Map()
    elements.filter(el => el.type === "image").forEach(el => {
      if (cache.has(el.src)) {
        result.set(el.src, cache.get(el.src))
      } else {
        const img = new window.Image()
        img.src = el.src
        cache.set(el.src, img)
        result.set(el.src, img)
      }
    })
    return result
  }, [elements])

  //
  // - useModalStates() for modal visibility

  // Text editing overlay state (still needed locally)
  const [editingTextId, setEditingTextId] = useState(null)
  const [editingTextValue, setEditingTextValue] = useState("")
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0, width: 0, height: 0, fontSize: 20 })
  const [editingLayerTextId, setEditingLayerTextId] = useState(null)
  const [editingLayerTextValue, setEditingLayerTextValue] = useState("")

  // Image name editing state
  const [editingImageNameId, setEditingImageNameId] = useState(null)
  const [editingImageNameValue, setEditingImageNameValue] = useState("")

  const exportAsImage = async (element, imageType = "png") => {
    const canvas = await html2canvas(element)
    const imageData = canvas.toDataURL(`image/${imageType}`)

    // Create a download link
    const link = document.createElement("a")
    link.href = imageData
    link.download = `design.${imageType}`
    link.click()
  }

  const exportAsPDF = async element => {
    canvasState.setShowGuides(false)
    await new Promise(resolve => setTimeout(resolve, 50)) // allow React to re-render
    const canvas = await html2canvas(element)
    const imageData = canvas.toDataURL("image/png")

    const pdf = new jsPDF("p", "mm", "a4")
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()

    // Calculate scaling to fit the canvas to the PDF page
    const canvasAspectRatio = canvas.width / canvas.height
    const pdfAspectRatio = pdfWidth / pdfHeight

    let imgWidth, imgHeight

    if (canvasAspectRatio > pdfAspectRatio) {
      // Fit to width
      imgWidth = pdfWidth
      imgHeight = pdfWidth / canvasAspectRatio
    } else {
      // Fit to height
      imgHeight = pdfHeight
      imgWidth = pdfHeight * canvasAspectRatio
    }

    // Center the image on the page
    const x = (pdfWidth - imgWidth) / 2
    const y = (pdfHeight - imgHeight) / 2

    pdf.addImage(imageData, "PNG", x, y, imgWidth, imgHeight)
    pdf.save("design.pdf")
    canvasState.setShowGuides(true)
  }

  // Constrain elements when canvas dimensions change
  const prevDimensions = useRef({ width: canvasState.canvasDimensions.width, height: canvasState.canvasDimensions.height })

  useEffect(() => {
    // Only run if dimensions actually changed
    if (
      prevDimensions.current.width === canvasState.canvasDimensions.width &&
      prevDimensions.current.height === canvasState.canvasDimensions.height
    ) {
      return
    }

    prevDimensions.current = { width: canvasState.canvasDimensions.width, height: canvasState.canvasDimensions.height }

    if (elements.length === 0) return

    const needsUpdate = elements.some(el => el.x > canvasState.canvasDimensions.width - 50 || el.y > canvasState.canvasDimensions.height - 50)

    if (needsUpdate) {
      const updatedElements = elements.map(el => ({
        ...el,
        x: Math.min(el.x, canvasState.canvasDimensions.width - 50),
        y: Math.min(el.y, canvasState.canvasDimensions.height - 50),
      }))
      setElements(updatedElements)
    }
  }, [canvasState.canvasDimensions.width, canvasState.canvasDimensions.height, elements, setElements])
  //
  // Check if current user is admin in selected studio
  const isCurrentUserStudioAdmin = () => {
    if (!studioManagement.selectedStudio) return false
    return studioManagement.selectedStudio.userRole === STUDIO_ROLES.OWNER || studioManagement.selectedStudio.userRole === STUDIO_ROLES.ADMIN
  }
  const openDesignPreview = async design => {
    let enrichedDesign = { ...design }
    if (design.createdBy && !design.createdByName) {
      try {
        const userDoc = await getDoc(doc(db, "users", design.createdBy))
        if (userDoc.exists()) {
          enrichedDesign.createdByName = userDoc.data().displayName || userDoc.data().email || design.createdBy
        }
      } catch (e) {
        // Fall back to UID
      }
    }
    modalStates.setPreviewDesign(enrichedDesign)
    modalStates.setShowDesignPreview(true)
  }

  // Load a specific design
  const loadDesign = async designId => {
    try {
      const designRef = doc(db, "designs", designId)
      const designSnap = await getDoc(designRef)
      if (designSnap.exists()) {
        const designData = designSnap.data()
        setElements(designData.elements)
        modalStates.setShowDesignGallery(false)
        toast.success("Design loaded successfully!")
      } else {
        toast.error("Design not found")
      }
    } catch (err) {
      console.error("Error loading design:", err.message)
      toast.error("Failed to load design")
    }
  }

  // Delete design confirmation state
  const [pendingDeleteDesignId, setPendingDeleteDesignId] = useState(null)

  // Delete a design — show confirmation modal
  const deleteDesign = designId => {
    setPendingDeleteDesignId(designId)
  }

  // Actually delete the design after confirmation
  const confirmDeleteDesign = async () => {
    const designId = pendingDeleteDesignId
    setPendingDeleteDesignId(null)
    if (!designId) return

    try {
      await deleteDesignUtil(designId, currentUser.uid)

      // Refresh the appropriate design list
      if (studioManagement.designContext === "studio" && studioManagement.selectedStudio) {
        await studioManagement.loadStudioDesigns(studioManagement.selectedStudio.id)
        studioManagement.setStudioDesigns(prev => prev.filter(design => design.id !== designId))
      } else {
        await designManagement.loadUserDesigns()
        designManagement.setUserDesigns(prev => prev.filter(design => design.id !== designId))
      }

      toast.success("Design deleted successfully!")
    } catch (err) {
      console.error("Error deleting design:", err.message)
      toast.error("Failed to delete design")
    }
  }

  // Rename a design
  const renameDesign = async (designId, newName) => {
    if (!newName.trim()) {
      toast.warning("Design name cannot be empty")
      return
    }

    try {
      await updateDesign(designId, { name: newName.trim() }, currentUser.uid)

      // Update the appropriate design list
      if (studioManagement.designContext === "studio" && studioManagement.selectedStudio) {
        studioManagement.setStudioDesigns(prev => prev.map(design => (design.id === designId ? { ...design, name: newName.trim() } : design)))
      } else {
        designManagement.setUserDesigns(prev => prev.map(design => (design.id === designId ? { ...design, name: newName.trim() } : design)))
      }

      designManagement.setEditingDesignName(null)
      designManagement.setEditingDesignNameValue("")
      toast.success("Design renamed successfully!")
    } catch (err) {
      console.error("Error renaming design:", err.message)
      toast.error("Failed to rename design")
    }
  }

  // Start editing design name
  const startEditingDesignName = design => {
    designManagement.setEditingDesignName(design.id)
    designManagement.setEditingDesignNameValue(
      design.name || `Design ${design.id.split("_")[1] ? new Date(parseInt(design.id.split("_")[1])).toLocaleDateString() : "Unknown"}`,
    )
  }

  // Finish editing design name
  const finishEditingDesignName = () => {
    if (designManagement.editingDesignName && designManagement.editingDesignNameValue.trim()) {
      renameDesign(designManagement.editingDesignName, designManagement.editingDesignNameValue)
    } else {
      designManagement.setEditingDesignName(null)
      designManagement.setEditingDesignNameValue("")
    }
  }

  // Use refs to hold latest hook references and avoid re-render loops
  const designManagementRef = useRef(designManagement)
  const studioManagementRef = useRef(studioManagement)
  useEffect(() => { designManagementRef.current = designManagement }, [designManagement])
  useEffect(() => { studioManagementRef.current = studioManagement }, [studioManagement])

  // Generate thumbnails for all designs
  const generateAllThumbnails = useCallback(async () => {
    const dm = designManagementRef.current
    const sm = studioManagementRef.current
    // Generate thumbnails for personal designs
    for (const design of dm.userDesigns) {
      if (!dm.designThumbnails[design.id]) {
        await dm.generateThumbnail(design)
      }
    }

    // Generate thumbnails for studio designs
    for (const design of sm.studioDesigns) {
      if (!dm.designThumbnails[design.id]) {
        await dm.generateThumbnail(design)
      }
    }
  }, [])

  // Load designs when user changes
  useEffect(() => {
    if (currentUser) {
      designManagementRef.current.loadUserDesigns()
      studioManagementRef.current.loadUserStudios()
    }
  }, [currentUser])

  // Generate thumbnails when designs are loaded
  useEffect(() => {
    if (designManagement.userDesigns.length > 0) {
      generateAllThumbnails()
    }
  }, [designManagement.userDesigns, generateAllThumbnails])

  // Generate thumbnails when studio designs are loaded
  useEffect(() => {
    if (studioManagement.studioDesigns.length > 0) {
      generateAllThumbnails()
    }
  }, [studioManagement.studioDesigns, generateAllThumbnails])

  useEffect(() => {
    if (transformerRef.current) {
      if (canvasState.selectedElementIds.length > 0) {
        const nodes = canvasState.selectedElementIds.map(id => stageRef.current.findOne(`#${id}`)).filter(Boolean)
        transformerRef.current.nodes(nodes)
        transformerRef.current.getLayer().batchDraw()
      } else {
        transformerRef.current.nodes([])
      }
    }
  }, [canvasState.selectedElementIds])

  // Update transformer when elements change (e.g., font size/family changes)
  useEffect(() => {
    if (transformerRef.current && canvasState.selectedElementIds.length > 0) {
      // Use setTimeout to ensure text nodes have finished rendering
      // This is especially important for text elements that need to calculate wrapping
      const timeoutId = setTimeout(() => {
        if (transformerRef.current && stageRef.current) {
          const nodes = canvasState.selectedElementIds.map(id => stageRef.current.findOne(`#${id}`)).filter(Boolean)
          transformerRef.current.nodes(nodes)
          transformerRef.current.forceUpdate()
          transformerRef.current.getLayer().batchDraw()
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [elements, canvasState.selectedElementIds])

  // Force stage redraw when elements change to ensure text wrapping is calculated
  useEffect(() => {
    if (stageRef.current) {
      // Small delay to ensure all text nodes have calculated their dimensions
      const timeoutId = setTimeout(() => {
        if (stageRef.current) {
          stageRef.current.batchDraw()
        }
      }, 10)

      return () => clearTimeout(timeoutId)
    }
  }, [elements])

  const addText = () => {
    const newText = {
      id: `text_${Date.now()}`,
      type: "text",
      x: 100,
      y: 100,
      text: "New Text",
      fontSize: canvasState.textFontSize || 20,
      fontFamily: canvasState.textFontFamily || "Arial",
      fill: "black",
      rotation: 0,
    }
    const newElements = addToHistory([...elements, newText])
    setElements(newElements)
    canvasState.setSelectedElementId(newText.id) // Automatically select the newly added text
  }

  // Open save dialog
  const openSaveDialog = useCallback(() => {
    if (!currentUser) {
      toast.warning("Please log in to save designs")
      return
    }
    modalStates.setShowSaveDialog(true)
    modalStates.setSaveDesignName("Untitled Design")
    // Default to the user's studio if they have one, otherwise Personal
    const defaultStudio = studioManagement.userStudios.length > 0 ? studioManagement.userStudios[0].id : null
    modalStates.setSaveToStudio(defaultStudio)
  }, [currentUser, modalStates, toast, studioManagement.userStudios])

  // Actually save the design
  const saveDesign = async () => {
    if (!modalStates.saveDesignName.trim()) {
      toast.warning("Please enter a design name")
      return
    }

    try {
      await saveDesignUtil(
        {
          elements,
          name: modalStates.saveDesignName.trim(),
        },
        currentUser.uid,
        modalStates.saveToStudio,
      )

      toast.success("Design saved successfully!")
      modalStates.setShowSaveDialog(false)

      // Refresh the appropriate design list
      if (modalStates.saveToStudio) {
        await studioManagement.loadStudioDesigns(modalStates.saveToStudio)
      } else {
        await designManagement.loadUserDesigns()
      }
    } catch (err) {
      console.error("Error saving design:", err.message)
      toast.error("Failed to save design")
    }
  }

  const uploadImage = event => {
    const files = Array.from(event.target.files)

    // Check if any files were actually selected (not cancelled)
    if (files.length === 0) {
      return
    }

    // Process each file
    let processedCount = 0
    const newImages = []

    const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

    files.forEach((file, index) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" is too large (max 5MB). Try compressing the image first.`)
        processedCount++
        if (processedCount === files.length && newImages.length > 0) {
          const newElements = addToHistory([...elements, ...newImages])
          setElements(newElements)
        }
        return // skip this file
      }

      const reader = new FileReader()

      reader.onerror = () => {
        toast.error(`Failed to read "${file.name}". Please try again.`)
        processedCount++
      }

      reader.onload = () => {
        const img = new window.Image()
        img.onload = () => {
          const offset = index * 20

          // Scale image to fit reasonably on canvas, preserving aspect ratio
          const maxSize = 400
          let width = img.naturalWidth
          let height = img.naturalHeight

          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }

          newImages.push({
            id: `image${Date.now()}_${index}`,
            type: "image",
            name: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
            x: 150 + offset,
            y: 150 + offset,
            src: reader.result,
            width,
            height,
          })

          processedCount++

          // Once all files are processed, add them to the canvas
          if (processedCount === files.length) {
            const newElements = addToHistory([...elements, ...newImages])
            setElements(newElements)

            // Clear the file input so the filename(s) disappear
            if (fileInputRef.current) {
              fileInputRef.current.value = ""
            }
          }
        }
        img.src = reader.result
      }

      reader.readAsDataURL(file)
    })

    // Handle edge case where all files were skipped (e.g., all too large)
    if (processedCount === files.length && newImages.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  const handleTransformEnd = () => {
    const node = stageRef.current.findOne(`#${canvasState.selectedElementId}`)
    if (!node) return

    const updatedElements = elements.map(el => {
      if (el.id === canvasState.selectedElementId) {
        // Handle text elements differently - update fontSize and width based on scale
        if (el.type === "text") {
          const scaleX = node.scaleX()
          const scaleY = node.scaleY()

          // Use scaleY for font size scaling (vertical scaling)
          const newFontSize = Math.round((el.fontSize || 20) * scaleY)

          // Use scaleX for width scaling (horizontal scaling)
          const newWidth = node.width() * scaleX

          const updated = {
            ...el,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
            fontSize: newFontSize,
            width: newWidth,
          }

          // Reset the node's scale to 1
          node.scaleX(1)
          node.scaleY(1)

          return updated
        } else {
          // For non-text elements (images, shapes), update width/height
          const newWidth = node.width() * node.scaleX()
          const newHeight = node.height() * node.scaleY()

          const updated = {
            ...el,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
            width: newWidth,
            height: newHeight,
          }

          // Reset the node's scale to 1 so it doesn't compound on next transform
          node.scaleX(1)
          node.scaleY(1)

          return updated
        }
      }
      return el
    })

    // Save the current state to history and apply the updated state
    const newElements = addToHistory(updatedElements)
    setElements(newElements)
  }

  const deleteSelectedElement = useCallback(() => {
    if (canvasState.selectedElementId) {
      // Use addToHistory to save the current state before deleting
      const newElements = addToHistory(elements.filter(el => !canvasState.selectedElementId.includes(el.id)))
      setElements(newElements)

      // Clear the Transformer nodes
      if (transformerRef.current) {
        transformerRef.current.nodes([])
      }

      canvasState.setSelectedElementId(null) // Deselect after deletion
    }
  }, [canvasState, elements, addToHistory, setElements])

  const onDragMove = e => {
    const node = e.target
    const stage = stageRef.current

    if (!stage) return

    const canvasWidth = stage.width()
    const canvasHeight = stage.height()

    const tolerance = 10 // Tolerance range for snapping
    const x = node.x()
    const y = node.y()

    // For text nodes, use getClientRect() which gives actual rendered bounds
    // For other elements, use node.width()/height() with scaling
    let width, height, offsetX, offsetY
    if (node.className === 'Text') {
      const clientRect = node.getClientRect({ skipTransform: true })
      width = clientRect.width
      height = clientRect.height
      // Account for offset between node position and clientRect position
      offsetX = clientRect.x
      offsetY = clientRect.y
    } else {
      width = node.width() * node.scaleX()
      height = node.height() * node.scaleY()
      offsetX = 0
      offsetY = 0
    }

    const snappedPosition = { x, y }
    let snappedToVerticalCenter = false
    let snappedToHorizontalCenter = false

    // Check horizontal centerline snapping (vertical center line - snaps x axis)
    if (Math.abs(x + offsetX + width / 2 - canvasWidth / 2) <= tolerance) {
      snappedPosition.x = canvasWidth / 2 - width / 2 - offsetX // Snap to horizontal centerline
      snappedToVerticalCenter = true
    }

    // Check vertical centerline snapping (horizontal center line - snaps y axis)
    if (Math.abs(y + offsetY + height / 2 - canvasHeight / 2) <= tolerance) {
      snappedPosition.y = canvasHeight / 2 - height / 2 - offsetY // Snap to vertical centerline
      snappedToHorizontalCenter = true
    }

    // Check snapping to cut line edges (red line boundaries)
    const cutlineLeft = CUTLINE_MARGIN
    const cutlineRight = canvasWidth - CUTLINE_MARGIN
    const cutlineTop = CUTLINE_MARGIN
    const cutlineBottom = canvasHeight - CUTLINE_MARGIN

    if (Math.abs(x + offsetX - cutlineLeft) <= tolerance) {
      snappedPosition.x = cutlineLeft - offsetX // Snap to left cut line
    } else if (Math.abs(x + offsetX + width - cutlineRight) <= tolerance) {
      snappedPosition.x = cutlineRight - width - offsetX // Snap to right cut line
    }

    if (Math.abs(y + offsetY - cutlineTop) <= tolerance) {
      snappedPosition.y = cutlineTop - offsetY // Snap to top cut line
    } else if (Math.abs(y + offsetY + height - cutlineBottom) <= tolerance) {
      snappedPosition.y = cutlineBottom - height - offsetY // Snap to bottom cut line
    }

    // Update snap guide highlighting
    canvasState.setSnappedAxes({ x: snappedToVerticalCenter, y: snappedToHorizontalCenter })

    // Apply snapped position
    node.x(snappedPosition.x)
    node.y(snappedPosition.y)
  }

  const onDragEnd = (e, elId) => {
    const stage = stageRef.current
    const canvasWidth = stage.width()
    const canvasHeight = stage.height()

    const centerX = canvasWidth / 2
    const centerY = canvasHeight / 2

    // For text nodes, use getClientRect() for actual rendered bounds
    const node = e.target
    let width, height, offsetX, offsetY
    if (node.className === 'Text') {
      const clientRect = node.getClientRect({ skipTransform: true })
      width = clientRect.width
      height = clientRect.height
      offsetX = clientRect.x
      offsetY = clientRect.y
    } else {
      width = node.width() * node.scaleX()
      height = node.height() * node.scaleY()
      offsetX = 0
      offsetY = 0
    }

    // Finalize snapping logic
    const snappedElements = elements.map(item => {
      if (item.id === elId) {
        let snappedX = node.x()
        let snappedY = node.y()
        if (Math.abs(snappedX + offsetX + width / 2 - centerX) <= SNAP_THRESHOLD) {
          snappedX = centerX - width / 2 - offsetX
        }
        if (Math.abs(snappedY + offsetY + height / 2 - centerY) <= SNAP_THRESHOLD) {
          snappedY = centerY - height / 2 - offsetY
        }
        return {
          ...item,
          x: snappedX,
          y: snappedY,
        }
      }
      return item
    })

    // Reset snap guide highlighting
    canvasState.setSnappedAxes({ x: false, y: false })

    // Save final position in history
    const newElements = addToHistory(snappedElements)
    setElements(newElements)
  }

  const renderSnapGuides = () => (
    <Layer>
      {/* Vertical Centerline (snaps x-axis) */}
      <Line
        points={[canvasState.canvasDimensions.width / 2, 0, canvasState.canvasDimensions.width / 2, canvasState.canvasDimensions.height]}
        stroke={canvasState.snappedAxes.x ? "blue" : "gray"}
        strokeWidth={canvasState.snappedAxes.x ? 2 : 1}
        dash={canvasState.snappedAxes.x ? [] : [4, 4]}
      />
      {/* Horizontal Centerline (snaps y-axis) */}
      <Line
        points={[0, canvasState.canvasDimensions.height / 2, canvasState.canvasDimensions.width, canvasState.canvasDimensions.height / 2]}
        stroke={canvasState.snappedAxes.y ? "blue" : "gray"}
        strokeWidth={canvasState.snappedAxes.y ? 2 : 1}
        dash={canvasState.snappedAxes.y ? [] : [4, 4]}
      />
      {/* Cut line snap guides */}
      <Line
        points={[CUTLINE_MARGIN, 0, CUTLINE_MARGIN, canvasState.canvasDimensions.height]}
        stroke="red"
        strokeWidth={1}
        dash={[2, 2]}
        opacity={0.5}
      />
      <Line
        points={[
          canvasState.canvasDimensions.width - CUTLINE_MARGIN,
          0,
          canvasState.canvasDimensions.width - CUTLINE_MARGIN,
          canvasState.canvasDimensions.height,
        ]}
        stroke="red"
        strokeWidth={1}
        dash={[2, 2]}
        opacity={0.5}
      />
      <Line
        points={[0, CUTLINE_MARGIN, canvasState.canvasDimensions.width, CUTLINE_MARGIN]}
        stroke="red"
        strokeWidth={1}
        dash={[2, 2]}
        opacity={0.5}
      />
      <Line
        points={[
          0,
          canvasState.canvasDimensions.height - CUTLINE_MARGIN,
          canvasState.canvasDimensions.width,
          canvasState.canvasDimensions.height - CUTLINE_MARGIN,
        ]}
        stroke="red"
        strokeWidth={1}
        dash={[2, 2]}
        opacity={0.5}
      />
    </Layer>
  )

  useEffect(() => {
    const handleKeyDown = e => {
      // Ignore key events when user is typing in an input, textarea, or contentEditable
      const tag = e.target.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return

      const isMod = e.metaKey || e.ctrlKey

      if (e.key === "Backspace" && canvasState.selectedElementId) {
        e.preventDefault()
        deleteSelectedElement()
      }

      // Ctrl/Cmd+Z → Undo
      if (isMod && e.key === "z" && !e.shiftKey) {
        e.preventDefault()
        const prev = undo()
        if (prev) setElements(prev)
      }

      // Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z → Redo
      if (isMod && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        e.preventDefault()
        const next = redo()
        if (next) setElements(next)
      }

      // Ctrl/Cmd+S → Save (prevent browser save dialog)
      if (isMod && e.key === "s") {
        e.preventDefault()
        openSaveDialog()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [canvasState.selectedElementId, deleteSelectedElement, undo, redo, setElements, openSaveDialog])

  const moveLayerUp = elementId => {
    const index = elements.findIndex(el => el.id === elementId)
    if (index < elements.length - 1) {
      const newElements = [...elements]
      const [movedElement] = newElements.splice(index, 1)
      newElements.splice(index + 1, 0, movedElement)
      const updatedElements = addToHistory(newElements)
      setElements(updatedElements)
    }
  }

  const moveLayerDown = elementId => {
    const index = elements.findIndex(el => el.id === elementId)
    if (index > 0) {
      const newElements = [...elements]
      const [movedElement] = newElements.splice(index, 1)
      newElements.splice(index - 1, 0, movedElement)
      const updatedElements = addToHistory(newElements)
      setElements(updatedElements)
    }
  }

  // Handler for starting text edit in layers
  const startLayerTextEdit = el => {
    setEditingLayerTextId(el.id)
    setEditingLayerTextValue(el.text)
  }
  // Handler for saving text edit in layers
  const finishLayerTextEdit = () => {
    setElements(els => els.map(el => (el.id === editingLayerTextId ? { ...el, text: editingLayerTextValue } : el)))
    setEditingLayerTextId(null)
    setEditingLayerTextValue("")
  }

  // Handler for starting image name edit in layers
  const startImageNameEdit = el => {
    setEditingImageNameId(el.id)
    setEditingImageNameValue(el.name || "image")
  }
  // Handler for saving image name edit in layers
  const finishImageNameEdit = () => {
    setElements(els => els.map(el => (el.id === editingImageNameId ? { ...el, name: editingImageNameValue } : el)))
    setEditingImageNameId(null)
    setEditingImageNameValue("")
  }

  const renderLayersPanel = () => (
    <div className="layers-panel bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-700">Layers</h3>
        <p className="text-xs text-gray-500 mt-1">Drag elements to reorder or use the controls</p>
      </div>
      <div className="divide-y divide-gray-100">
        {[...elements].reverse().map((el, index) => {
          const originalIndex = elements.length - 1 - index
          const isNeighbor =
            canvasState.draggedOverIndex !== null &&
            ((index === canvasState.draggedOverIndex - 1 && canvasState.dropPosition === "above") ||
              (index === canvasState.draggedOverIndex + 1 && canvasState.dropPosition === "below"))

          return (
            <div
              key={el.id}
              draggable
              onDragStart={e => {
                e.dataTransfer.setData("text/plain", el.id)
                e.target.classList.add("opacity-50")
                canvasState.setDraggedOverIndex(null)
                canvasState.setDropPosition(null)
              }}
              onDragEnd={e => {
                e.target.classList.remove("opacity-50")
                canvasState.setDraggedOverIndex(null)
                canvasState.setDropPosition(null)
              }}
              onDragOver={e => {
                e.preventDefault()
                const rect = e.currentTarget.getBoundingClientRect()
                const mouseY = e.clientY
                const threshold = rect.height / 2

                canvasState.setDraggedOverIndex(index)
                canvasState.setDropPosition(mouseY < rect.top + threshold ? "above" : "below")
              }}
              onDragLeave={e => {
                canvasState.setDraggedOverIndex(null)
                canvasState.setDropPosition(null)
              }}
              onDrop={e => {
                e.preventDefault()
                const draggedId = e.dataTransfer.getData("text/plain")
                const targetId = el.id

                canvasState.setDraggedOverIndex(null)
                canvasState.setDropPosition(null)

                if (draggedId !== targetId) {
                  const draggedIndex = elements.findIndex(item => item.id === draggedId)
                  const targetIndex = elements.findIndex(item => item.id === targetId)

                  const newElements = [...elements]
                  const [draggedElement] = newElements.splice(draggedIndex, 1)
                  newElements.splice(targetIndex, 0, draggedElement)

                  const updatedElements = addToHistory(newElements)
                  setElements(updatedElements)
                }
              }}
              className={`flex items-center justify-between p-3 cursor-move transition-all duration-200 ${
                canvasState.selectedElementId === el.id ? "bg-blue-50" : "hover:bg-gray-50"
              } ${isNeighbor ? "bg-blue-100" : ""} ${canvasState.draggedOverIndex === index ? "border-2 border-blue-500" : ""}`}
              style={{
                transform: isNeighbor
                  ? index === canvasState.draggedOverIndex - 1 && canvasState.dropPosition === "above"
                    ? "translateY(-8px)"
                    : index === canvasState.draggedOverIndex + 1 && canvasState.dropPosition === "below"
                      ? "translateY(8px)"
                      : ""
                  : "",
              }}
            >
              <div className="flex items-center space-x-3">
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => moveLayerUp(el.id)}
                    disabled={originalIndex === elements.length - 1}
                    className={`p-1 rounded-full ${
                      originalIndex === elements.length - 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                    }`}
                    title="Move up"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <span className="text-xs font-medium text-gray-500">{index + 1}</span>
                  <button
                    onClick={() => moveLayerDown(el.id)}
                    disabled={originalIndex === 0}
                    className={`p-1 rounded-full ${
                      originalIndex === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                    }`}
                    title="Move down"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-col">
                  {/* If text element and editing, show input */}
                  {el.type === "text" && editingLayerTextId === el.id ? (
                    <input
                      type="text"
                      value={editingLayerTextValue}
                      autoFocus
                      onChange={e => setEditingLayerTextValue(e.target.value)}
                      onBlur={finishLayerTextEdit}
                      onKeyDown={e => {
                        if (e.key === "Enter") finishLayerTextEdit()
                        if (e.key === "Escape") setEditingLayerTextId(null)
                      }}
                      className="border px-2 py-1 rounded text-sm"
                      style={{ minWidth: 80 }}
                    />
                  ) : el.type === "image" && editingImageNameId === el.id ? (
                    <input
                      type="text"
                      value={editingImageNameValue}
                      autoFocus
                      onChange={e => setEditingImageNameValue(e.target.value)}
                      onBlur={finishImageNameEdit}
                      onKeyDown={e => {
                        if (e.key === "Enter") finishImageNameEdit()
                        if (e.key === "Escape") setEditingImageNameId(null)
                      }}
                      className="border px-2 py-1 rounded text-sm"
                      style={{ minWidth: 80 }}
                    />
                  ) : (
                    <span className="text-sm font-medium text-gray-700 flex items-center">
                      {el.type === "text" ? el.text : el.type === "image" ? el.name || el.type : el.type}
                      {/* Show edit icon for text elements */}
                      {el.type === "text" && (
                        <button
                          onClick={() => startLayerTextEdit(el)}
                          className="ml-2 text-gray-400 hover:text-blue-500"
                          title="Edit text"
                          tabIndex={-1}
                        >
                          <svg
                            width="18"
                            height="18"
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            viewBox="0 0 24 24"
                          >
                            <path d="M15.232 5.232l3.536 3.536M9 11l6.586-6.586a2 2 0 112.828 2.828L11.828 13.828a2 2 0 01-2.828 0L9 13V11z" />
                          </svg>
                        </button>
                      )}
                      {/* Show edit icon for image elements */}
                      {el.type === "image" && (
                        <button
                          onClick={() => startImageNameEdit(el)}
                          className="ml-2 text-gray-400 hover:text-blue-500"
                          title="Edit name"
                          tabIndex={-1}
                        >
                          <svg
                            width="18"
                            height="18"
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            viewBox="0 0 24 24"
                          >
                            <path d="M15.232 5.232l3.536 3.536M9 11l6.586-6.586a2 2 0 112.828 2.828L11.828 13.828a2 2 0 01-2.828 0L9 13V11z" />
                          </svg>
                        </button>
                      )}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">{(() => {
                    const typeLabels = { text: "Text", image: "Image", rect: "Rectangle", circle: "Circle", line: "Line" };
                    const label = typeLabels[el.type] || el.type;
                    const sameTypeBefore = elements.filter((e, i) => e.type === el.type && i < elements.indexOf(el));
                    return `${label} ${sameTypeBefore.length + 1}`;
                  })()}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  if (canvasState.selectedElementId === el.id) {
                    // Deselect if already selected
                    canvasState.setSelectedElementId(null)
                    canvasState.setSelectedElementIds([])
                  } else {
                    // Select if not selected
                    canvasState.setSelectedElementId(el.id)
                    canvasState.setSelectedElementIds([el.id])
                  }
                }}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  canvasState.selectedElementId === el.id ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {canvasState.selectedElementId === el.id ? "Selected" : "Select"}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )

  const handleStageMouseDown = e => {
    // Check if we're clicking on the stage itself or a non-interactive element
    const target = e.target
    const isStage = target === target.getStage()
    const isNonInteractive = target.attrs?.stroke === "red" || target.attrs?.stroke === "green"

    // Start selection if clicking on stage or non-interactive elements
    if (isStage || isNonInteractive) {
      const stage = e.target.getStage()
      const point = stage.getPointerPosition()

      canvasState.setIsSelecting(true)
      selectionStartRef.current = point
      canvasState.setSelectionRect({
        x: point.x,
        y: point.y,
        width: 0,
        height: 0,
      })

      // Clear current selection when starting new selection
      canvasState.setSelectedElementIds([])
      canvasState.setSelectedElementId(null)
    }
  }

  const handleStageMouseMove = e => {
    if (!canvasState.isSelecting) {
      return
    }

    const stage = e.target.getStage()
    const point = stage.getPointerPosition()
    const startPos = selectionStartRef.current

    if (point && startPos) {
      const newRect = {
        x: Math.min(startPos.x, point.x),
        y: Math.min(startPos.y, point.y),
        width: Math.abs(point.x - startPos.x),
        height: Math.abs(point.y - startPos.y),
      }
      canvasState.setSelectionRect(newRect)
    }
  }

  const handleStageMouseUp = e => {
    if (!canvasState.isSelecting) {
      return
    }

    // Find elements within selection rectangle
    const selectedElements = elements.filter(el => {
      const element = stageRef.current.findOne(`#${el.id}`)
      if (!element) return false

      // Get the element's position and size
      const elementBox = element.getClientRect()
      const elementPos = {
        x: elementBox.x,
        y: elementBox.y,
        width: elementBox.width,
        height: elementBox.height,
      }

      // Get the selection box position and size
      const selectionBox = {
        x: canvasState.selectionRect.x,
        y: canvasState.selectionRect.y,
        width: canvasState.selectionRect.width,
        height: canvasState.selectionRect.height,
      }

      // Check if the element intersects with the selection box
      const intersects =
        elementPos.x < selectionBox.x + selectionBox.width &&
        elementPos.x + elementPos.width > selectionBox.x &&
        elementPos.y < selectionBox.y + selectionBox.height &&
        elementPos.y + elementPos.height > selectionBox.y

      return intersects
    })

    // Update selection state
    if (selectedElements.length > 0) {
      const selectedIds = selectedElements.map(el => el.id)
      canvasState.setSelectedElementIds(selectedIds)
      canvasState.setSelectedElementId(selectedIds[0]) // Keep first element as primary selection
    }

    // Reset selection
    canvasState.setIsSelecting(false)
    canvasState.setSelectionRect(null)
  }

  // Helper: get selected elements and their bounding box
  const getSelectedElements = () => elements.filter(el => canvasState.selectedElementIds.includes(el.id))
  const getGroupBoundingBox = () => {
    const selected = getSelectedElements()
    if (selected.length === 0) return null
    const xs = selected.map(el => el.x)
    const ys = selected.map(el => el.y)
    const ws = selected.map(el => (el.width !== undefined ? el.width : el.radius ? el.radius * 2 : 0))
    const hs = selected.map(el => (el.height !== undefined ? el.height : el.radius ? el.radius * 2 : 0))
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)
    const maxX = Math.max(...xs.map((x, i) => x + ws[i]))
    const maxY = Math.max(...ys.map((y, i) => y + hs[i]))
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }

  // Group drag handlers
  const handleGroupDragStart = e => {
    const box = getGroupBoundingBox()
    if (!box) return
    // Store offset of each element relative to group top-left
    groupDragOffset.current = {}
    getSelectedElements().forEach(el => {
      groupDragOffset.current[el.id] = { dx: el.x - box.x, dy: el.y - box.y }
    })
  }

  const handleGroupDragMove = e => {
    const box = getGroupBoundingBox()
    if (!box) return
    const groupNode = e.target
    const newX = groupNode.x()
    const newY = groupNode.y()
    // Move all selected elements relative to group
    setElements(prev =>
      prev.map(el => {
        if (canvasState.selectedElementIds.includes(el.id)) {
          const { dx, dy } = groupDragOffset.current[el.id] || { dx: 0, dy: 0 }
          return { ...el, x: newX + dx, y: newY + dy }
        }
        return el
      }),
    )
  }

  const handleGroupDragEnd = e => {
    const box = getGroupBoundingBox()
    if (!box) return
    const groupNode = e.target
    const newX = groupNode.x()
    const newY = groupNode.y()
    // Finalize all selected elements' positions
    const updated = elements.map(el => {
      if (canvasState.selectedElementIds.includes(el.id)) {
        const { dx, dy } = groupDragOffset.current[el.id] || { dx: 0, dy: 0 }
        return { ...el, x: newX + dx, y: newY + dy }
      }
      return el
    })
    const newElements = addToHistory(updated)
    setElements(newElements)
  }

  const clearCanvas = () => {
    clearElements()
    canvasState.setSelectedElementId(null)
    canvasState.setSelectedElementIds([])
    if (transformerRef.current) {
      transformerRef.current.nodes([])
    }
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Handle double-click to edit text
  const handleTextDblClick = (el, e) => {
    setEditingTextId(el.id)
    setEditingTextValue(el.text)
    // Get the absolute position of the text node
    const container = document.querySelector(".canvas-container")
    const containerBox = container.getBoundingClientRect()
    const stageBox = stageRef.current.container().getBoundingClientRect()
    const node = stageRef.current.findOne(`#${el.id}`)
    const absPos = node.getAbsolutePosition()
    const clientRect = node.getClientRect()
    // Adjust for canvasState.scale and container offset
    setInputPosition({
      x: absPos.x * canvasState.scale + (stageBox.left - containerBox.left),
      y: absPos.y * canvasState.scale + (stageBox.top - containerBox.top) - 4,
      width: clientRect.width * canvasState.scale,
      height: clientRect.height * canvasState.scale,
      fontSize: (el.fontSize || 20) * canvasState.scale,
    })
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus()
    }, 0)
  }

  // Handle input change
  const handleInputChange = e => {
    setEditingTextValue(e.target.value)
  }

  // Handle input blur or Enter
  const finishEditing = () => {
    if (editingTextId && editingTextValue !== undefined) {
      setElements(prevElements => {
        const updated = prevElements.map(el => (el.id === editingTextId ? { ...el, text: editingTextValue } : el))
        return updated
      })
    }
    setEditingTextId(null)
    setEditingTextValue("")
  }

  // Handle profile name editing
  const handleStartEditingName = () => {
    setEditingNameValue(currentUser?.displayName || "")
    setIsEditingName(true)
  }

  const handleSaveDisplayName = async () => {
    if (!editingNameValue.trim()) {
      toast.warning("Name cannot be empty")
      return
    }

    try {
      await updateDisplayName(editingNameValue.trim())
      setIsEditingName(false)
    } catch (error) {
      toast.error("Failed to update name: " + error.message)
    }
  }

  const handleCancelEditingName = () => {
    setIsEditingName(false)
    setEditingNameValue("")
  }

  // Handle clear canvas with confirmation
  const handleClearCanvas = () => {
    modalStates.setShowConfirmation(true)
  }

  const confirmClearCanvas = () => {
    clearCanvas()
    modalStates.setShowConfirmation(false)
  }

  // Handle export with file type selection
  const handleExport = fileType => {
    const canvasContainer = document.querySelector(".canvas-container")
    if (fileType === "png") {
      exportAsImage(canvasContainer, "png")
    } else if (fileType === "pdf") {
      exportAsPDF(canvasContainer)
    }
  }

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden">
      <div className="w-full md:w-80 flex-shrink-0 bg-gray-50 p-4 border-r overflow-y-auto">
        <div className="space-y-6">
          {/* User Profile */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-700">Profile</h2>
            </div>
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  {isEditingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingNameValue}
                        onChange={e => setEditingNameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            handleSaveDisplayName()
                          } else if (e.key === "Escape") {
                            handleCancelEditingName()
                          }
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <button onClick={handleSaveDisplayName} className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors" title="Save">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <button onClick={handleCancelEditingName} className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors" title="Cancel">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{currentUser?.displayName || "User"}</p>
                      <button
                        onClick={handleStartEditingName}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit name"
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">{currentUser?.email}</p>
                  {userProfile && (
                    <p className="text-xs text-blue-600 font-medium mt-1">
                      {userProfile.userType === USER_TYPES.STUDIO_ADMIN ? "Studio Admin" : "Regular User"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <NotificationBell
                    currentUser={currentUser}
                    onNotificationAction={async (notification) => {
                      if (notification.designId) {
                        try {
                          const designRef = doc(db, "designs", notification.designId)
                          const designSnap = await getDoc(designRef)
                          if (designSnap.exists()) {
                            const designData = { id: designSnap.id, ...designSnap.data() }
                            openDesignPreview(designData)
                          } else {
                            toast.error("Design not found — it may have been deleted")
                          }
                        } catch (err) {
                          console.error("Error loading design from notification:", err)
                          toast.error("Failed to load the design")
                        }
                      }
                    }}
                  />
                  <button onClick={logout} className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors">
                    Logout
                  </button>
                </div>
              </div>

              {/* Stats Section */}
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => modalStates.setShowDesignGallery(true)}
                  className="bg-blue-50 rounded-lg p-2 hover:bg-blue-100 transition-colors text-left group relative"
                >
                  <p className="text-xs text-gray-600">
                    {studioManagement.designContext === "studio" ? "Studio Designs" : "My Designs"}
                  </p>
                  <p className="text-lg font-bold text-blue-600">
                    {studioManagement.designContext === "studio"
                      ? studioManagement.studioDesigns.length
                      : designManagement.userDesigns.length}
                  </p>
                  <svg
                    className="w-3.5 h-3.5 text-blue-400 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => modalStates.setShowStudioManager(true)}
                  className="bg-purple-50 rounded-lg p-2 hover:bg-purple-100 transition-colors text-left group relative"
                >
                  {(() => {
                    const isAdminOfAnyStudio = studioManagement.userStudios.some(s => s.userRole === STUDIO_ROLES.OWNER || s.userRole === STUDIO_ROLES.ADMIN)
                    if (studioManagement.userStudios.length > 0 && !isAdminOfAnyStudio) {
                      return (
                        <>
                          <p className="text-xs text-gray-600">Studio</p>
                          <p className="text-sm font-bold text-purple-600 truncate">{studioManagement.userStudios[0].name}</p>
                        </>
                      )
                    }
                    return (
                      <>
                        <p className="text-xs text-gray-600">Studios</p>
                        <p className="text-lg font-bold text-purple-600">{studioManagement.userStudios.length}</p>
                      </>
                    )
                  })()}
                  <svg
                    className="w-3.5 h-3.5 text-purple-400 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Workspace Selector */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-700">Workspace</h2>
            </div>
            <div className="p-3">
              <div className="space-y-3">
                {/* Workspace Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Active Workspace</label>
                  <select
                    value={studioManagement.designContext}
                    onChange={e => {
                      studioManagement.setDesignContext(e.target.value)
                      if (e.target.value === "studio" && studioManagement.userStudios.length > 0) {
                        studioManagement.setSelectedStudio(studioManagement.userStudios[0])
                        studioManagement.loadStudioDesigns(studioManagement.userStudios[0].id)
                      } else if (e.target.value === "personal") {
                        studioManagement.setSelectedStudio(null)
                      }
                    }}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="personal">Personal Designs</option>
                    {studioManagement.userStudios.length > 0 && <option value="studio">Studio</option>}
                  </select>
                </div>

                {studioManagement.designContext === "studio" && studioManagement.userStudios.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Studio</label>
                    <select
                      value={studioManagement.selectedStudio?.id || ""}
                      onChange={e => {
                        const studio = studioManagement.userStudios.find(s => s.id === e.target.value)
                        studioManagement.setSelectedStudio(studio)
                        if (studio) {
                          studioManagement.loadStudioDesigns(studio.id)
                        }
                      }}
                      className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {studioManagement.userStudios.map(studio => (
                        <option key={studio.id} value={studio.id}>
                          {studio.name} ({studio.userRole})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Canvas Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-700">Canvas Settings</h2>
            </div>
            <div className="p-3">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Canvas Size</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={canvasState.canvasDimensions.width === 750 && canvasState.canvasDimensions.height === 1050 ? "portrait" : "landscape"}
                  onChange={e => {
                    const selectedMode = e.target.value
                    if (selectedMode === "portrait") {
                      canvasState.setCanvasDimensions({ width: 750, height: 1050 })
                    } else if (selectedMode === "landscape") {
                      canvasState.setCanvasDimensions({ width: 1050, height: 750 })
                    }
                  }}
                >
                  <option value="portrait">Portrait (750 x 1050)</option>
                  <option value="landscape">Landscape (1050 x 750)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tools Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-700">Tools</h2>
            </div>
            <div className="p-3 space-y-4">
              {/* Text Tool */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Text</h3>

                {/* Font Family Selector */}
                <div className="mb-2">
                  <label className="block text-xs text-gray-600 mb-1">Font</label>
                  <FontSelector value={canvasState.textFontFamily} onChange={canvasState.setTextFontFamily} />
                </div>

                {/* Font Size Selector */}
                <div className="mb-2">
                  <label className="block text-xs text-gray-600 mb-1">Size</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={canvasState.textFontSize}
                    onChange={e => {
                      const value = e.target.value
                      // Allow empty string or numbers only
                      if (value === "" || /^\d+$/.test(value)) {
                        canvasState.setTextFontSize(value)
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.target.blur()
                      }
                    }}
                    onBlur={e => {
                      // On blur, ensure we have a valid number
                      const value = e.target.value
                      const numValue = parseInt(value)
                      if (value === "" || isNaN(numValue) || numValue < 8) {
                        canvasState.setTextFontSize(20)
                      } else if (numValue > 200) {
                        canvasState.setTextFontSize(200)
                      } else {
                        canvasState.setTextFontSize(numValue)
                      }
                    }}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="8-200"
                  />
                </div>

                <button onClick={addText} className="w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors">
                  Add Text
                </button>
              </div>

              {/* Edit Selected Text */}
              {(() => {
                const selectedElement = elements.find(el => el.id === canvasState.selectedElementId)
                if (selectedElement && selectedElement.type === "text") {
                  return (
                    <div className="border-t pt-4 mt-4 bg-blue-50 -mx-3 px-3 pb-3 rounded-b-lg border-l-4 border-l-blue-500">
                      <h3 className="text-sm font-medium text-blue-700 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        Edit Selected Text
                      </h3>

                      {/* Edit Font Family */}
                      <div className="mb-2">
                        <label className="block text-xs text-gray-600 mb-1">Font</label>
                        <FontSelector
                          value={selectedElement.fontFamily || "Arial"}
                          onChange={fontFamily => {
                            const newElements = addToHistory(elements.map(el => (el.id === selectedElement.id ? { ...el, fontFamily } : el)))
                            setElements(newElements)
                          }}
                        />
                      </div>

                      {/* Edit Font Size */}
                      <div className="mb-2">
                        <label className="block text-xs text-gray-600 mb-1">Size</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={selectedElement.fontSize === "" || selectedElement.fontSize === undefined ? "" : selectedElement.fontSize}
                          onChange={e => {
                            const value = e.target.value
                            // Allow empty string or numbers only, don't add to history yet
                            if (value === "" || /^\d+$/.test(value)) {
                              const newElements = elements.map(el => (el.id === selectedElement.id ? { ...el, fontSize: value } : el))
                              setElements(newElements)
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              e.target.blur()
                            }
                          }}
                          onBlur={e => {
                            // On blur, ensure we have a valid number and add to history
                            const value = e.target.value
                            const numValue = parseInt(value)
                            let finalValue = 20
                            if (value !== "" && !isNaN(numValue) && numValue >= 8) {
                              finalValue = numValue > 200 ? 200 : numValue
                            }
                            const newElements = addToHistory(
                              elements.map(el => (el.id === selectedElement.id ? { ...el, fontSize: finalValue } : el)),
                            )
                            setElements(newElements)
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="8-200"
                        />
                      </div>

                      {/* Text Alignment */}
                      <div className="mb-2">
                        <label className="block text-xs text-gray-600 mb-1">Alignment</label>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              const newElements = addToHistory(elements.map(el => (el.id === selectedElement.id ? { ...el, align: "left" } : el)))
                              setElements(newElements)
                            }}
                            className={`flex-1 px-2 py-1 border rounded text-sm transition-colors ${
                              (selectedElement.align || "left") === "left"
                                ? "bg-blue-500 text-white border-blue-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                            title="Align Left"
                          >
                            <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 4h14v2H3V4zm0 4h10v2H3V8zm0 4h14v2H3v-2zm0 4h10v2H3v-2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              const newElements = addToHistory(elements.map(el => (el.id === selectedElement.id ? { ...el, align: "center" } : el)))
                              setElements(newElements)
                            }}
                            className={`flex-1 px-2 py-1 border rounded text-sm transition-colors ${
                              selectedElement.align === "center"
                                ? "bg-blue-500 text-white border-blue-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                            title="Align Center"
                          >
                            <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 4h14v2H3V4zm2 4h10v2H5V8zm-2 4h14v2H3v-2zm2 4h10v2H5v-2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              const newElements = addToHistory(elements.map(el => (el.id === selectedElement.id ? { ...el, align: "right" } : el)))
                              setElements(newElements)
                            }}
                            className={`flex-1 px-2 py-1 border rounded text-sm transition-colors ${
                              selectedElement.align === "right"
                                ? "bg-blue-500 text-white border-blue-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                            title="Align Right"
                          >
                            <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 4h14v2H3V4zm4 4h10v2H7V8zm-4 4h14v2H3v-2zm4 4h10v2H7v-2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              const newElements = addToHistory(elements.map(el => (el.id === selectedElement.id ? { ...el, align: "justify" } : el)))
                              setElements(newElements)
                            }}
                            className={`flex-1 px-2 py-1 border rounded text-sm transition-colors ${
                              selectedElement.align === "justify"
                                ? "bg-blue-500 text-white border-blue-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                            title="Justify"
                          >
                            <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3 4h14v2H3V4zm0 4h14v2H3V8zm0 4h14v2H3v-2zm0 4h14v2H3v-2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              })()}

              {/* Shapes */}
              {/* <div> 
                <h3 className="text-sm font-medium text-gray-700 mb-2">Shapes</h3>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className="border rounded-md p-2 text-center bg-white hover:bg-gray-50 transition-colors"
                    onClick={() =>
                      addToHistory([
                        ...elements,
                        { id: `rect${elements.length + 1}`, type: "rect", x: 150, y: 150, width: 100, height: 100, fill: "blue" },
                      ])
                    }
                  >
                    Rectangle
                  </button>
                  <button
                    className="border rounded-md p-2 text-center bg-white hover:bg-gray-50 transition-colors"
                    onClick={() =>
                      addToHistory([...elements, { id: `circle${elements.length + 1}`, type: "circle", x: 150, y: 150, radius: 50, fill: "green" }])
                    }
                  >
                    Circle
                  </button>
                  <button
                    className="border rounded-md p-2 text-center bg-white hover:bg-gray-50 transition-colors"
                    onClick={() =>
                      addToHistory([
                        ...elements,
                        { id: `line${elements.length + 1}`, type: "line", points: [50, 50, 200, 200], stroke: "red", strokeWidth: 5 },
                      ])
                    }
                  >
                    Line
                  </button>
                </div>
              </div>
              */}

              {/* Images */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Images</h3>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={uploadImage}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          </div>

          {/* Layers Panel */}
          {renderLayersPanel()}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-700">Actions</h2>
            </div>
            <div className="p-3 space-y-3">
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const prev = undo()
                    if (prev) setElements(prev)
                  }}
                  disabled={history.length === 0}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  Undo
                  <FaUndo className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    const next = redo()
                    if (next) setElements(next)
                  }}
                  disabled={redoStack.length === 0}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <FaRedo className="w-4 h-4" />
                  Redo
                </button>
              </div>
              <button
                onClick={openSaveDialog}
                className="w-full bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
              >
                <FaSave className="w-5 h-5" />
                Save Design
              </button>
              <button
                onClick={() => modalStates.setShowExportModal(true)}
                className="w-full bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export
              </button>
              <button
                onClick={handleClearCanvas}
                className="w-full bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Clear Canvas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex-1 flex flex-col bg-gray-100 p-4">
        {/* Guidance banner for non-admin studio members */}
        {showGuidanceBanner && studioManagement.userStudios.length > 0 && !studioManagement.userStudios.some(s => s.userRole === STUDIO_ROLES.OWNER || s.userRole === STUDIO_ROLES.ADMIN) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mx-4 mb-2 flex items-start justify-between">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-700">
                Create your ad design, then open <strong>My Designs</strong> and tap <strong>&quot;Submit Ad to Studio&quot;</strong> to send it for the recital program.
              </p>
            </div>
            <button
              onClick={() => { setShowGuidanceBanner(false); localStorage.setItem("adGuidanceDismissed", "true") }}
              className="text-blue-400 hover:text-blue-600 ml-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex-1 flex justify-center items-center">
        <div
          className="canvas-container relative bg-white shadow-lg"
          style={{
            width: `${canvasState.canvasDimensions.width}px`,
            height: `${canvasState.canvasDimensions.height}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Render input overlay for editing text */}
          {editingTextId && (
            <input
              ref={inputRef}
              type="text"
              value={editingTextValue}
              onChange={handleInputChange}
              onBlur={finishEditing}
              onKeyDown={e => {
                if (e.key === "Enter") finishEditing()
                if (e.key === "Escape") finishEditing()
              }}
              style={{
                position: "absolute",
                left: inputPosition.x,
                top: inputPosition.y,
                width: inputPosition.width,
                height: inputPosition.height,
                fontSize: inputPosition.fontSize,
                fontFamily: "inherit",
                border: "1px solid #007bff",
                background: "white",
                zIndex: 10,
                padding: "2px 6px",
                outline: "none",
                borderRadius: 4,
                boxSizing: "border-box",
              }}
            />
          )}
          <Stage
            width={canvasState.canvasDimensions.width}
            height={canvasState.canvasDimensions.height}
            className="border border-gray-300"
            scaleX={canvasState.scale}
            scaleY={canvasState.scale}
            ref={stageRef}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleStageMouseUp}
            onClick={e => {
              // Only handle click if we're not selecting
              if (!canvasState.isSelecting) {
                const clickedElement = e.target?.attrs?.id
                if (clickedElement) {
                  // If shift key is pressed, add to selection
                  if (e.evt.shiftKey) {
                    canvasState.setSelectedElementIds(prev => {
                      const newSelection = prev.includes(clickedElement) ? prev.filter(id => id !== clickedElement) : [...prev, clickedElement]
                      return newSelection
                    })
                  } else {
                    // Single selection
                    canvasState.setSelectedElementIds([clickedElement])
                  }
                  canvasState.setSelectedElementId(clickedElement)
                } else {
                  // Clicked on stage, clear selection
                  canvasState.setSelectedElementIds([])
                  canvasState.setSelectedElementId(null)
                }
              }
            }}
          >
            {canvasState.showGuides && renderSnapGuides()}
            <Layer>
              {/* Canvas elements layer */}
              {canvasState.showGuides && (
                <>
                  {/* Center Line */}
                  <Line
                    points={[canvasState.canvasDimensions.width / 2, 0, canvasState.canvasDimensions.width / 2, canvasState.canvasDimensions.height]}
                    stroke="transparent"
                    strokeWidth={2}
                    dash={[5, 5]}
                  />
                  {/* Cut Line */}
                  <Rect
                    x={CUTLINE_MARGIN}
                    y={CUTLINE_MARGIN}
                    width={canvasState.canvasDimensions.width - CUTLINE_MARGIN * 2}
                    height={canvasState.canvasDimensions.height - CUTLINE_MARGIN * 2}
                    stroke="red"
                    strokeWidth={2}
                    dash={[10, 5]}
                  />
                  {/* Safe Zone */}
                  <Rect
                    x={SAFEZONE_MARGIN}
                    y={SAFEZONE_MARGIN}
                    width={canvasState.canvasDimensions.width - SAFEZONE_MARGIN * 2}
                    height={canvasState.canvasDimensions.height - SAFEZONE_MARGIN * 2}
                    stroke="green"
                    strokeWidth={2}
                  />
                </>
              )}
              {/* If multiple selected, render them in a draggable group */}
              {canvasState.selectedElementIds.length > 1 ? (
                <Group
                  x={getGroupBoundingBox().x}
                  y={getGroupBoundingBox().y}
                  draggable
                  onDragStart={handleGroupDragStart}
                  onDragMove={handleGroupDragMove}
                  onDragEnd={handleGroupDragEnd}
                  id="multi-group"
                >
                  {getSelectedElements().map(el => {
                    const relX = el.x - getGroupBoundingBox().x
                    const relY = el.y - getGroupBoundingBox().y
                    if (el.type === "text") {
                      return (
                        <Text
                          key={el.id}
                          id={el.id}
                          x={relX}
                          y={relY}
                          text={editingTextId === el.id ? editingTextValue : el.text}
                          fontSize={el.fontSize}
                          fontFamily={el.fontFamily || "Arial"}
                          textDecoration={el.textDecoration || "none"}
                          align={el.align || "left"}
                          fill={el.fill}
                          rotation={el.rotation}
                          {...(el.width ? { width: el.width } : {})}
                          wrap="word"
                          onDblClick={e => handleTextDblClick(el, e)}
                        />
                      )
                    } else if (el.type === "image") {
                      return <KonvaImage key={el.id} id={el.id} x={relX} y={relY} width={el.width} height={el.height} image={loadedImages.get(el.src)} />
                    } else if (el.type === "rect") {
                      return <Rect key={el.id} id={el.id} x={relX} y={relY} width={el.width} height={el.height} fill={el.fill} />
                    } else if (el.type === "circle") {
                      return <Circle key={el.id} id={el.id} x={relX} y={relY} radius={el.radius} fill={el.fill} />
                    } else if (el.type === "line") {
                      return (
                        <Line
                          key={el.id}
                          id={el.id}
                          points={el.points.map((p, i) => (i % 2 === 0 ? p - getGroupBoundingBox().x : p - getGroupBoundingBox().y))}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                        />
                      )
                    }
                    return null
                  })}
                  {/* Transformer will be attached to this group */}
                </Group>
              ) : (
                // Single or no selection: render all elements normally
                elements.map(el => {
                  const isSelected = canvasState.selectedElementIds.includes(el.id)
                  const commonProps = {
                    id: el.id,
                    x: el.x,
                    y: el.y,
                    draggable: canvasState.selectedElementIds.length <= 1,
                    onClick: e => {
                      if (!canvasState.isSelecting) {
                        if (e.evt.shiftKey) {
                          canvasState.setSelectedElementIds(prev => {
                            const newSelection = prev.includes(el.id) ? prev.filter(id => id !== el.id) : [...prev, el.id]
                            return newSelection
                          })
                        } else {
                          canvasState.setSelectedElementIds([el.id])
                        }
                        canvasState.setSelectedElementId(el.id)
                      }
                    },
                    onDragMove: onDragMove,
                    onDragEnd: e => onDragEnd(e, el.id),
                    stroke: isSelected ? "blue" : "transparent",
                    strokeWidth: isSelected ? 2 : 0,
                  }
                  if (el.type === "text") {
                    return (
                      <Text
                        key={el.id}
                        {...commonProps}
                        text={editingTextId === el.id ? editingTextValue : el.text}
                        fontSize={el.fontSize}
                        fontFamily={el.fontFamily || "Arial"}
                        textDecoration={el.textDecoration || "none"}
                        align={el.align || "left"}
                        fill={el.fill}
                        rotation={el.rotation}
                        {...(el.width ? { width: el.width } : {})}
                        wrap="word"
                        onDblClick={e => handleTextDblClick(el, e)}
                      />
                    )
                  } else if (el.type === "image") {
                    return <KonvaImage key={el.id} {...commonProps} image={loadedImages.get(el.src)} width={el.width} height={el.height} />
                  } else if (el.type === "rect") {
                    return <Rect key={el.id} {...commonProps} width={el.width} height={el.height} fill={el.fill} />
                  } else if (el.type === "circle") {
                    return <Circle key={el.id} {...commonProps} radius={el.radius} fill={el.fill} />
                  } else if (el.type === "line") {
                    return <Line key={el.id} {...commonProps} points={el.points} stroke={el.stroke} strokeWidth={el.strokeWidth} />
                  }
                  return null
                })
              )}

              {/* Attach transformer to group or single node */}
              <Transformer
                ref={transformerRef}
                boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 20 || newBox.height < 20) {
                    return oldBox
                  }
                  return newBox
                }}
                onTransformEnd={handleTransformEnd}
              />
            </Layer>

            {/* Selection rectangle layer - always on top */}
            <Layer>
              {canvasState.showGuides && canvasState.selectionRect && (
                <>
                  <Rect
                    x={canvasState.selectionRect.x}
                    y={canvasState.selectionRect.y}
                    width={canvasState.selectionRect.width}
                    height={canvasState.selectionRect.height}
                    fill="rgba(255, 0, 0, 0.05)"
                    stroke="red"
                    strokeWidth={3}
                    dash={[5, 5]}
                    listening={false}
                  />
                  {/* <Text
                    x={canvasState.selectionRect.x}
                    y={canvasState.selectionRect.y - 20}
                    text={`Selection: ${Math.round(canvasState.selectionRect.width)}x${Math.round(canvasState.selectionRect.height)}`}
                    fontSize={14}
                    fill="red"
                  /> */}
                </>
              )}
            </Layer>
          </Stage>
        </div>
        </div>
      </div>

      {/* Design Gallery Modal */}
      {modalStates.showDesignGallery && (
        <DesignGalleryModal
          designs={studioManagement.designContext === "studio" ? studioManagement.studioDesigns : designManagement.userDesigns}
          designContext={studioManagement.designContext}
          selectedStudio={studioManagement.selectedStudio}
          isAdmin={isCurrentUserStudioAdmin()}
          currentUserId={currentUser?.uid}
          designThumbnails={designManagement.designThumbnails}
          editingDesignName={designManagement.editingDesignName}
          editingDesignNameValue={designManagement.editingDesignNameValue}
          setEditingDesignNameValue={designManagement.setEditingDesignNameValue}
          onStartEditingName={startEditingDesignName}
          onFinishEditingName={finishEditingDesignName}
          onCancelEditingName={() => {
            designManagement.setEditingDesignName(null)
            designManagement.setEditingDesignNameValue("")
          }}
          onLoadDesign={loadDesign}
          onDeleteDesign={deleteDesign}
          onSetPreferredDesign={async (designId) => {
            const result = await studioManagement.handleSetPreferredDesign(designId)
            if (result?.action === "submitted") {
              toast.success("Ad submitted to studio!")
            } else if (result?.action === "unsubmitted") {
              toast.info("Ad submission withdrawn")
            } else if (result === false) {
              toast.error("Failed to update submission")
            }
          }}
          onClose={() => modalStates.setShowDesignGallery(false)}
        />
      )}

      {/* Studio Manager Modal */}
      {modalStates.showStudioManager && <StudioManager onClose={() => modalStates.setShowStudioManager(false)} />}

      {/* Save Design Dialog */}
      {modalStates.showSaveDialog && (
        <SaveDesignDialog
          saveDesignName={modalStates.saveDesignName}
          setSaveDesignName={modalStates.setSaveDesignName}
          saveToStudio={modalStates.saveToStudio}
          setSaveToStudio={modalStates.setSaveToStudio}
          userStudios={studioManagement.userStudios}
          onSave={saveDesign}
          onClose={() => modalStates.setShowSaveDialog(false)}
        />
      )}

      {/* Member Design Status Modal */}
      {modalStates.showMemberView && (
        <MemberDesignModal
          selectedStudio={studioManagement.selectedStudio}
          studioMembersWithDesigns={studioManagement.studioMembersWithDesigns}
          designThumbnails={designManagement.designThumbnails}
          onPreviewDesign={openDesignPreview}
          onClose={() => modalStates.setShowMemberView(false)}
        />
      )}

      {/* Design Preview Modal */}
      {modalStates.showDesignPreview && modalStates.previewDesign && (
        <DesignPreviewModal
          previewDesign={modalStates.previewDesign}
          designThumbnails={designManagement.designThumbnails}
          onLoadDesign={designId => {
            loadDesign(designId)
            modalStates.setShowDesignPreview(false)
            modalStates.setShowMemberView(false)
          }}
          onClose={() => modalStates.setShowDesignPreview(false)}
        />
      )}

      {/* Export Modal */}
      {modalStates.showExportModal && <ExportModal onExport={handleExport} onClose={() => modalStates.setShowExportModal(false)} />}

      {/* Confirmation Modal - Clear Canvas */}
      {modalStates.showConfirmation && (
        <ConfirmationModal
          title="Clear Canvas"
          message="Are you sure you want to clear the canvas? This action cannot be undone."
          confirmText="Clear Canvas"
          cancelText="Cancel"
          onConfirm={confirmClearCanvas}
          onCancel={() => modalStates.setShowConfirmation(false)}
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}

      {/* Confirmation Modal - Delete Design */}
      {pendingDeleteDesignId && (
        <ConfirmationModal
          title="Delete Design"
          message="Are you sure you want to delete this design? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteDesign}
          onCancel={() => setPendingDeleteDesignId(null)}
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
    </div>
  )
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ToastProvider>
  )
}

function AppContent() {
  const { currentUser } = useAuth()

  if (!currentUser) {
    return <Auth />
  }

  return (
    <ErrorBoundary>
      <MainApp />
    </ErrorBoundary>
  )
}

export default App
